import React, {
    createContext, Dispatch, FormEvent, memo, ReactNode, SetStateAction, useCallback, useContext, useEffect, useMemo,
    useState,
} from "react";
import { ApolloError } from "@apollo/client";
import {
    ActionContextType,
    useGetInstalledWorkflowSystemActionsQuery,
    useSaveWorkflowMutation,
    WorkflowServiceAction,
    WorkflowStep,
    WorkflowStepGroup,
    Workflow,
    FormField,
    WorkflowService,
} from "../../../__generated__/api";
import useRouting, { useParams } from "../../../contexts/Routing";
import { createWorklowStep } from "../../workflow/Workflow";
import createId from "../../../create-id";
import {
    findNodeAndRemoveCard, findNodeAndPushCard, findNodeAndReplaceCard, findNodeAndReorderCards,
} from "../workflow/flow-actions";
import useDirtyTracker, { DirtyTracker } from "../../dirty-tracker/DirtyTracker";
import { LoggingHandler } from "../../../contexts/logging/LoggingContext";
import { ACTION } from "../workflow/nodes";

export type WorkflowStepWithParentId = WorkflowStep & { parentId: string | null };
export type WorkflowStepGroupWithNodeData = WorkflowStepGroup & { id: string, parentId: string | null };

interface WorkflowContextType {
    formFields?: FormField[];
    workflow?: Workflow;
    error?: ApolloError;
    services: WorkflowServiceAction[];
    servicesLoading: boolean;
    temporaryNodeId: string | null;
    setTemporaryNodeId: (nodeId: string | null) => void;
    initWorkflow: () => Promise<void>;
    currentStep: WorkflowStepGroupWithNodeData | null;
    setCurrentStep: (step: WorkflowStepGroupWithNodeData | null) => void;
    selectedNode: WorkflowStepWithParentId | null;
    setSelectedNode: (node: WorkflowStepWithParentId | null) => void;
    deleteNodeFromWorkflow: ({ nodeId, shouldMarkDirty }: { nodeId: string; shouldMarkDirty: boolean }) => void;
    addNodeToWorkflow: ({
        newNode, parentNodeId, index, shouldMarkDirty,
    }: {
        newNode: WorkflowStep; parentNodeId: string; index?: number; shouldMarkDirty: boolean;
    }) => void;
    replaceNodeInWorkflow: (cardId: string, replacement: WorkflowStep) => void;
    dirtyTracker: DirtyTracker | null;
    clearSelection: () => void;
    serviceConfiguration: WorkflowServiceAction | null;
    setServiceConfiguration: (service: WorkflowServiceAction | null) => void;
    selectedService: WorkflowService | null;
    setSelectedService: (service: WorkflowService | null) => void;
    addTemporaryNode: ({ nodeId, index }: { nodeId: string; index?: number }) => void;
    updateNodeChildrenInWorkflow: (nodeId: string, sortedChildren: WorkflowStep[]) => void;
    collapsedGroups: Set<string>;
    setCollapsedGroups: Dispatch<SetStateAction<Set<string>>>;
    ghostParentAndIndex: GhostParentAndIndex | null;
    setGhostParentAndIndex: Dispatch<SetStateAction<GhostParentAndIndex | null>>;
    actionsContextType: ActionContextType | undefined;
}

export const WorkflowContext = createContext<WorkflowContextType>({
    formFields: undefined,
    workflow: undefined,
    error: undefined,
    services: [],
    servicesLoading: false,
    temporaryNodeId: null,
    setTemporaryNodeId: () => {
        // do nothing
    },
    initWorkflow: () => Promise.resolve(),
    currentStep: null,
    setCurrentStep: () => {
        // do nothing
    },
    selectedNode: null,
    setSelectedNode: () => {
        // do nothing
    },
    deleteNodeFromWorkflow: () => {
        // do nothing
    },
    addNodeToWorkflow: () => {
        // do nothing
    },
    replaceNodeInWorkflow: () => {
        // do nothing
    },
    dirtyTracker: null,
    clearSelection: () => {
        // do nothing
    },
    serviceConfiguration: null,
    setServiceConfiguration: () => {
        // do nothing
    },
    selectedService: null,
    setSelectedService: () => {
        // do nothing
    },
    addTemporaryNode: () => {
        // do nothing
    },
    updateNodeChildrenInWorkflow: () => {
        // do nothing
    },
    collapsedGroups: new Set(),
    setCollapsedGroups: () => {
        // do nothing
    },
    ghostParentAndIndex: null,
    setGhostParentAndIndex: () => {
        // do nothing
    },
    actionsContextType: undefined,
});

const CREATED_ID = "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,request-created)";
const CREATING_ID = "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,request-creating)";
const ACTION_GROUP = "urn:fq:workflow-service-action-child:(urn:fq:workflow-service-action:(urn:fq:workflow-service:system,request-created),actions)";
const ACTIONS_CREATING_ID = "urn:fq:workflow-service-action-child:(urn:fq:workflow-service-action:"
    + "(urn:fq:workflow-service:system,request-creating),actions)";

const ACTION_CREATING_REQUEST = {
    id: "",
    name: "Creating request",
    actionId: CREATING_ID,
    action: {
        id: CREATING_ID,
        name: "Request Creating",
        description: "Request first created",
        allowedChildren: [
            {
                id: ACTIONS_CREATING_ID,
                label: "Actions",
                description: "",
            },
        ],
    },
    serviceId: "urn:fq:workflow-service:system",
    description: "The automation to execute while the request is being created by the end user. "
        + "This can handle actions such conditionally showing or hiding fields.",
    parameters: [],
};

const ACTION_REQUEST_CREATED: WorkflowStep = {
    id: "",
    __typename: "WorkflowStep",
    name: "Request Created",
    actionId: CREATED_ID,
    action: {
        id: CREATED_ID,
        name: "Request Created",
        description: "Request first created",
        allowedChildren: [
            {
                id: ACTION_GROUP,
                label: "Actions",
                description: "",
            },
        ],
    },
    children: [{
        __typename: "WorkflowStepGroup",
        groupId: ACTION_GROUP,
        children: [],
    }],
    serviceId: "urn:fq:workflow-service:system",
    description: "The Automations to execute after the user has created the request. Common actions "
        + "include routing requests to appropriate people and sending approvals.",
    parameters: [],
};

function createEmptyWorkflow(actionsContextType: ActionContextType | undefined) {
    const workflow = {
        ...(actionsContextType === ActionContextType.Request ? ACTION_CREATING_REQUEST : ACTION_REQUEST_CREATED),
        id: `urn:fq:workflow-step:(urn:fq:workflow:-1,${createId()})`,
    };

    return workflow;
}

interface GhostParentAndIndex {
    parentId: string;
    index: number;
}

interface WorkflowProviderProps {
    children: ReactNode;
    onSaved?: ({ workflowId, shouldReloadForm }: { workflowId?: string; shouldReloadForm?: boolean }) => void;
    workflow?: Workflow;
    formFields?: FormField[];
    actionsContextType?: ActionContextType;
}

function WorkflowProvider({
    children, onSaved, workflow, formFields, actionsContextType,
}: WorkflowProviderProps) {
    const { Paths } = useRouting();
    const { id: formId } = useParams(Paths.ADMINISTRATOR_SETTING_ID);
    const [saveWorkflowMutation] = useSaveWorkflowMutation();
    const [temporaryNodeId, setTemporaryNodeId] = useState<string | null>(null);
    const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
    const [currentStep, setCurrentStep] = useState<WorkflowStepGroupWithNodeData | null>(null);
    const [ghostParentAndIndex, setGhostParentAndIndex] = useState<GhostParentAndIndex | null>(null);
    const [selectedNode, setSelectedNode] = useState<WorkflowStepWithParentId | null>(null);
    const [serviceConfiguration, setServiceConfiguration] = useState<WorkflowServiceAction | null>(null);
    const [selectedService, setSelectedService] = useState<WorkflowService | null>(null);

    const { data: services, loading: servicesLoading } = useGetInstalledWorkflowSystemActionsQuery({
        variables: {
            type: actionsContextType ?? ActionContextType.Ticket,
        },
    });

    const [draftWorkflow, setDraftWorkflow] = useState(() => structuredClone(workflow));

    const syncWorkflow = useCallback(() => {
        const clonedWorkflow = structuredClone(workflow);

        const initialNode = actionsContextType === ActionContextType.Request
            ? clonedWorkflow?.steps?.find(step => step.actionId === ACTION.REQUEST_CREATING)
            : clonedWorkflow?.steps?.firstElement;

        if (!initialNode) {
            return;
        }

        const actionsGroupNode = initialNode?.children?.firstElement;
        const actionsGroupNodeChildren = actionsGroupNode?.children;

        if (!actionsGroupNodeChildren || actionsGroupNodeChildren.length === 0) {
            const tempNodeId = `urn:fq:workflow-step:(urn:fq:workflow:-1,${createId()})`;

            actionsGroupNodeChildren?.push({
                __typename: "WorkflowStep",
                id: tempNodeId,
                actionId: ACTION.NEW,
                name: "Temp",
            });
            setTemporaryNodeId(tempNodeId);
        }

        setDraftWorkflow(clonedWorkflow);
    }, [workflow, actionsContextType]);

    useEffect(() => {
        syncWorkflow();
    }, [workflow]);

    const dirtyTracker = useDirtyTracker({
        finishedSavingMessage: "Finished saving Action",
        onSave: (event: FormEvent) => {
            event.preventDefault();
            (async () => {
                const savedWorkflow = await saveWorkflowMutation({
                    variables: {
                        id: draftWorkflow?.id,
                        workflow: {
                            name: "Auto generated",
                            description: "Auto generated for form",
                            steps: draftWorkflow?.steps?.map(createWorklowStep),
                        },
                    },
                });

                onSaved?.({
                    workflowId: savedWorkflow.data?.saveWorkflow?.id,
                    shouldReloadForm: false,
                });

                dirtyTracker.clean();
            })();
        },
        onReset: () => {
            syncWorkflow();
        },
    });

    const initWorkflow = useCallback(async () => {
        try {
            const newWorkflow = createEmptyWorkflow(actionsContextType);
            const newSteps = [...(draftWorkflow?.steps ?? []), newWorkflow].map(createWorklowStep);

            const savedWorkflow = await saveWorkflowMutation({
                variables: {
                    id: draftWorkflow?.id,
                    workflow: {
                        name: "Auto generated",
                        description: "Auto generated for form",
                        steps: newSteps,
                    },
                },
            });

            onSaved?.({
                workflowId: savedWorkflow.data?.saveWorkflow?.id,
                shouldReloadForm: true,
            });
        } catch (err) {
            LoggingHandler.instance.error({ err }, "Error initializing workflow");
        }
    }, [draftWorkflow, formId]);

    const clearSelection = useCallback(() => {
        setSelectedNode(null);
        setTemporaryNodeId(null);
        setCurrentStep(null);
    }, []);

    const deleteNodeFromWorkflow = useCallback(({ nodeId, shouldMarkDirty }: { nodeId: string; shouldMarkDirty: boolean }) => {
        const rootSteps = (draftWorkflow?.steps ?? []) as WorkflowStep[];

        if (rootSteps.isEmpty) {
            return;
        }

        rootSteps.forEach(step => findNodeAndRemoveCard(step, nodeId));

        setDraftWorkflow(structuredClone(draftWorkflow));

        clearSelection();

        if (shouldMarkDirty) {
            dirtyTracker.dirty();
        }
    }, [draftWorkflow]);

    const addNodeToWorkflow = useCallback(({
        newNode, parentNodeId, index, shouldMarkDirty,
    }: {
        newNode: WorkflowStep; parentNodeId: string; index?: number; shouldMarkDirty: boolean;
    }) => {
        const rootSteps = (draftWorkflow?.steps ?? []) as WorkflowStep[];

        if (rootSteps.length === 0) {
            return;
        }
        rootSteps.forEach(step => {
            findNodeAndPushCard({
                step,
                newCard: newNode,
                newCardPartNodeId: parentNodeId,
                currentStepParentId: draftWorkflow?.id,
                index,
            });
        });

        setDraftWorkflow(structuredClone(draftWorkflow));

        clearSelection();

        if (shouldMarkDirty) {
            dirtyTracker.dirty();
        }
    }, [draftWorkflow]);

    const replaceNodeInWorkflow = useCallback((cardId: string, replacement: WorkflowStep) => {
        const rootSteps = (draftWorkflow?.steps ?? []) as WorkflowStep[];

        if (rootSteps.isEmpty) {
            return;
        }

        rootSteps.forEach(step => findNodeAndReplaceCard(step, cardId, replacement));

        setDraftWorkflow(structuredClone(draftWorkflow));

        clearSelection();

        dirtyTracker.dirty();
    }, [draftWorkflow]);

    const addTemporaryNode = useCallback(({ nodeId, index }: { nodeId: string; index?: number }) => {
        const rootSteps = (draftWorkflow?.steps ?? []) as WorkflowStep[];

        if (rootSteps.isEmpty) {
            return;
        }

        const workflowId = draftWorkflow?.id;
        const newNodeId = `urn:fq:workflow-step:(${workflowId},${createId()})`;

        const newCard = {
            __typename: "WorkflowStep" as const,
            id: newNodeId,
            actionId: ACTION.NEW,
            name: "Temp",
        };

        rootSteps.forEach(step => {
            findNodeAndPushCard({
                step,
                newCard,
                newCardPartNodeId: nodeId,
                currentStepParentId: workflowId,
                index,
            });
        });
        setTemporaryNodeId(newNodeId);
        setDraftWorkflow(structuredClone(draftWorkflow));
    }, [draftWorkflow]);

    const updateNodeChildrenInWorkflow = useCallback((nodeId: string, sortedChildren: WorkflowStep[]) => {
        const rootSteps = (draftWorkflow?.steps ?? []) as WorkflowStep[];

        if (rootSteps.isEmpty) {
            return;
        }

        rootSteps.forEach(step => findNodeAndReorderCards(step, sortedChildren, nodeId));

        setDraftWorkflow(structuredClone(draftWorkflow));

        dirtyTracker.dirty();
    }, [draftWorkflow]);

    const workflowServiceSystemActions = useMemo(() => services?.workflowServiceSystemActions ?? [], [services]);

    const value = useMemo(() => ({
        workflow: draftWorkflow,
        formFields,
        services: workflowServiceSystemActions,
        servicesLoading,
        temporaryNodeId,
        setTemporaryNodeId,
        initWorkflow,
        currentStep,
        setCurrentStep,
        selectedNode,
        setSelectedNode,
        deleteNodeFromWorkflow,
        addNodeToWorkflow,
        replaceNodeInWorkflow,
        dirtyTracker,
        clearSelection,
        serviceConfiguration,
        setServiceConfiguration,
        selectedService,
        setSelectedService,
        addTemporaryNode,
        updateNodeChildrenInWorkflow,
        collapsedGroups,
        setCollapsedGroups,
        ghostParentAndIndex,
        setGhostParentAndIndex,
        actionsContextType,
    }), [
        draftWorkflow,
        formFields,
        workflowServiceSystemActions,
        servicesLoading,
        temporaryNodeId,
        setTemporaryNodeId,
        initWorkflow,
        currentStep,
        setCurrentStep,
        selectedNode,
        setSelectedNode,
        deleteNodeFromWorkflow,
        addNodeToWorkflow,
        replaceNodeInWorkflow,
        dirtyTracker,
        clearSelection,
        serviceConfiguration,
        setServiceConfiguration,
        selectedService,
        setSelectedService,
        addTemporaryNode,
        updateNodeChildrenInWorkflow,
        collapsedGroups,
        setCollapsedGroups,
        ghostParentAndIndex,
        setGhostParentAndIndex,
        actionsContextType,
    ]);

    return (
        <WorkflowContext.Provider value={value}>
            {children}
        </WorkflowContext.Provider>
    );
}

export default memo(WorkflowProvider);

WorkflowProvider.defaultProps = {
    onSaved: undefined,
    workflow: undefined,
    formFields: undefined,
    actionsContextType: undefined,
};

export function useWorkflow() {
    return useContext(WorkflowContext);
}
